// Locale-aware video helper, in the same spirit as localizedDownloads.ts:
// return the localized asset when one exists, otherwise the English original.
//
// Every tour clip and episode is recorded in English and published again in
// es, de and fr on the YouTube channel, but the site only ever stored one id.
// A reader on /fr-FR was therefore sent to the English cut of a video that
// exists in their language, which is worse than having no localized version
// at all, because the effort of making one was already spent.

import type { SupportedLocale } from '@/i18n';

/**
 * A bare string means "one cut, English, for now". A map carries the variants
 * that exist, keyed by locale, and `default` is what every other locale gets -
 * so en-GB, en-SG and en-ZA fall through to the English cut without needing
 * their own entries.
 */
export type LocalizedVideo =
  | string
  | ({ default: string } & Partial<Record<SupportedLocale, string>>);

/** Resolve for the active locale, falling back rather than rendering nothing. */
export const resolveVideo = (
  value: LocalizedVideo | undefined,
  locale: string | undefined,
): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'string') return value;
  return value[locale as SupportedLocale] ?? value.default;
};

/** True when a real cut exists for this locale rather than the fallback. */
export const hasLocalizedVideo = (
  value: LocalizedVideo | undefined,
  locale: string | undefined,
): boolean =>
  Boolean(value && typeof value !== 'string' && value[locale as SupportedLocale]);

/**
 * Per-language playlists on the cSquare GRC channel.
 *
 * Verified live Sep 3 2026: the English tour holds 17 videos, the French 15,
 * all localized. The ids are 13 characters, which is shorter than the common
 * PL+32 form but is what the channel actually issues - both were opened and
 * confirmed rather than assumed.
 *
 * ja-JP points at the English playlist on purpose, and there will never be a
 * separate Japanese one: the Japanese work is a subtitle track plus a localized
 * title and description on the English videos, so a "Japanese playlist" would
 * hold the same recordings. Japanese readers get their version through the
 * embed parameters instead - see SUBTITLE_ONLY_LOCALES in ProductTourVideos.
 */
export const TOUR_PLAYLISTS: Record<SupportedLocale, string> = {
  'en-US': 'PLconk0EJ5Cx4',
  'es-MX': 'PLJucT7izhLCY',
  'de-DE': 'PLeLjkTNTZWy8',
  'fr-FR': 'PLRyKy-JdV1w0',
  'ja-JP': 'PLconk0EJ5Cx4',
};

/** Series playlists. English only so far. */
export const PLAYLISTS = {
  trailGuides: 'PLI7vdVq_Gzz8',
  randomActsOfAI: 'PLVUMzBI6Om-8',
  randomActsOfAIClips: 'PLGTRLswkdtHo',
} as const;

export const playlistUrl = (id: string) =>
  `https://www.youtube.com/playlist?list=${id}`;

/** The product tour playlist in the reader's language, English if there is none. */
export const tourPlaylistUrl = (locale: string | undefined) =>
  playlistUrl(TOUR_PLAYLISTS[locale as SupportedLocale] ?? TOUR_PLAYLISTS['en-US']);
